import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  fieldLabel: string;
  initialValue?: string;
  needsProdConfirm: boolean;
  onSubmit: (value: string, confirmation: string | undefined) => Promise<void>;
}

export function EnumValueDialog({
  open,
  onOpenChange,
  title,
  fieldLabel,
  initialValue,
  needsProdConfirm,
  onSubmit,
}: Props) {
  const [value, setValue] = useState(initialValue ?? '');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(value, needsProdConfirm ? confirmation : undefined);
      onOpenChange(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>{fieldLabel}</Label>
          <Input value={value} onChange={(e) => setValue(e.target.value)} />
          {needsProdConfirm && (
            <>
              <Label>Confirmation (type &quot;PROD ENUM 변경&quot;)</Label>
              <Input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
            </>
          )}
          {err && <div className="text-sm text-rose-600 dark:text-rose-400">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              !value.trim() || busy || (needsProdConfirm && confirmation !== 'PROD ENUM 변경')
            }
          >
            {busy ? 'Saving…' : 'OK'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
